import { reactive } from 'vue';

type ToastType = 'success' | 'error' | 'info' | 'warning';

const DURATION = 3500;
const ALERT_DURATION = 6000;

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

/**
 * A diagnostic note any tool can forward to the toast surface. `level` accepts
 * `'warn'` as an alias for `'warning'` so the `{ level, message }` shape that
 * data sources already emit (e.g. time-series sampling notes) maps directly.
 */
export interface ToastNote {
  level: ToastType | 'warn';
  message: string;
}

export class ToastService {
  public toasts = reactive<Toast[]>([]);
  /** Monotonic id source — `Date.now()` collides when a batch is dispatched in the same tick. */
  private nextId = 0;

  constructor() {
    this.toasts = reactive([]);
  }

  public success(message: string, duration: number = DURATION): void {
    this.addToast(message, 'success', duration);
  }

  public error(message: string, duration: number | undefined = undefined): void {
    // Automatically use longer duration for alerts
    const finalDuration = duration ?? (message.toLowerCase().includes('alert') ? ALERT_DURATION : DURATION);
    this.addToast(message, 'error', finalDuration);
  }

  public warning(message: string, duration: number = DURATION): void {
    this.addToast(message, 'warning', duration);
  }

  public info(message: string, duration: number = DURATION): void {
    this.addToast(message, 'info', duration);
  }

  /**
   * Dispatch a batch of diagnostic notes (e.g. a source's "N of M windows
   * failed to load" / "no data" messages) to the toast surface. Shared entry
   * point so any tool can forward `{ level, message }[]` without re-writing the
   * level→method dispatch every time. `dedupe` (default true) drops identical
   * messages within the batch — several series often emit the same note.
   */
  public notify(
    notes: ToastNote | ToastNote[] | null | undefined,
    options: { dedupe?: boolean; duration?: number } = {},
  ): void {
    if (!notes) {
      return;
    }
    const list = Array.isArray(notes) ? notes : [notes];
    const dedupe = options.dedupe ?? true;
    const seen = new Set<string>();
    for (const note of list) {
      if (!note?.message) {
        continue;
      }
      if (dedupe) {
        if (seen.has(note.message)) {
          continue;
        }
        seen.add(note.message);
      }
      const type: ToastType = note.level === 'warn' ? 'warning' : note.level;
      this.addToast(note.message, type, options.duration ?? DURATION);
    }
  }

  private addToast(message: string, type: ToastType, duration: number): void {
    const id = ++this.nextId;

    this.toasts.push({ id, message, type });

    setTimeout(() => this.removeToast(id), duration ?? DURATION);
  }

  public removeToast(id: number): void {
    const index = this.toasts.findIndex((toast) => toast.id === id);

    if (index !== -1) {
      this.toasts.splice(index, 1);
    }
  }
}

export default {
  install(app: any) {
    app.config.globalProperties.$toast = new ToastService();
  },
};
